import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useSearch } from "wouter";
import Sidebar from "@/components/sidebar";
import AssetDetailModal from "@/components/viral-gallery/asset-detail-modal";
import SequenceCard, { type SequenceListItem } from "@/components/stories-creator/sequence-card";
import SequenceDetailModal from "@/components/stories-creator/sequence-detail-modal";
import type {
  GalleryItem,
  GalleryListResponse,
  GalleryCategory,
  GalleryMediaTypeFilter,
} from "@/components/viral-gallery/types";
import {
  GALLERY_CATEGORIES,
  GALLERY_CATEGORY_LABEL_KEYS,
  GALLERY_MEDIA_TYPES,
} from "@/components/viral-gallery/types";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, ArrowUpRight, Play, Sparkles } from "lucide-react";
import { useTheme } from "@/components/theme-provider";

// ── per-category config ────────────────────────────────────────────────
const CATEGORY_STYLE: Record<
  GalleryCategory,
  {
    bg: string; // dark-theme section background gradient
    bgLight: string; // light-theme section background gradient
    glow: string; // dark-theme radial glow color
    glowLight: string; // light-theme radial glow color
    accent: string; // pill/badge accent
    descKey: string; // i18n key for short description shown on card
  }
> = {
  ads_ecommerce: {
    bg: "linear-gradient(145deg, #1f0a14 0%, #100008 100%)",
    bgLight: "linear-gradient(145deg, #fff5f8 0%, #ffffff 100%)",
    glow: "rgba(244,63,94,0.35)",
    glowLight: "rgba(244,63,94,0.12)",
    accent: "#fb7185",
    descKey: "viralGallery.category.ads_ecommerce",
  },
  ads_lead_generation: {
    bg: "linear-gradient(145deg, #051a1f 0%, #000d10 100%)",
    bgLight: "linear-gradient(145deg, #f0fbff 0%, #ffffff 100%)",
    glow: "rgba(6,182,212,0.35)",
    glowLight: "rgba(6,182,212,0.12)",
    accent: "#22d3ee",
    descKey: "viralGallery.category.ads_lead_generation",
  },
  video_talking_head: {
    bg: "linear-gradient(145deg, #12052a 0%, #0a0014 100%)",
    bgLight: "linear-gradient(145deg, #f8f4ff 0%, #ffffff 100%)",
    glow: "rgba(139,92,246,0.35)",
    glowLight: "rgba(139,92,246,0.12)",
    accent: "#a78bfa",
    descKey: "viralGallery.category.video_talking_head",
  },
  video_ugc_ecommerce: {
    bg: "linear-gradient(145deg, #1a0808 0%, #0d0000 100%)",
    bgLight: "linear-gradient(145deg, #fff4f3 0%, #ffffff 100%)",
    glow: "rgba(239,62,41,0.35)",
    glowLight: "rgba(239,62,41,0.12)",
    accent: "#ef3e29",
    descKey: "viralGallery.category.video_ugc_ecommerce",
  },
  video_ugc_software: {
    bg: "linear-gradient(145deg, #020d1f 0%, #000510 100%)",
    bgLight: "linear-gradient(145deg, #f0f6ff 0%, #ffffff 100%)",
    glow: "rgba(59,130,246,0.35)",
    glowLight: "rgba(59,130,246,0.12)",
    accent: "#60a5fa",
    descKey: "viralGallery.category.video_ugc_software",
  },
  ads_graphic_ai: {
    bg: "linear-gradient(145deg, #1a0a00 0%, #0d0500 100%)",
    bgLight: "linear-gradient(145deg, #fff7f0 0%, #ffffff 100%)",
    glow: "rgba(249,115,22,0.35)",
    glowLight: "rgba(249,115,22,0.12)",
    accent: "#fb923c",
    descKey: "viralGallery.category.ads_graphic_ai",
  },
  social_graphic: {
    bg: "linear-gradient(145deg, #1a0416 0%, #0d0009 100%)",
    bgLight: "linear-gradient(145deg, #fef4fb 0%, #ffffff 100%)",
    glow: "rgba(236,72,153,0.35)",
    glowLight: "rgba(236,72,153,0.12)",
    accent: "#f472b6",
    descKey: "viralGallery.category.social_graphic",
  },
  social_organic: {
    bg: "linear-gradient(145deg, #031408 0%, #000d03 100%)",
    bgLight: "linear-gradient(145deg, #f2fdf5 0%, #ffffff 100%)",
    glow: "rgba(34,197,94,0.35)",
    glowLight: "rgba(34,197,94,0.12)",
    accent: "#4ade80",
    descKey: "viralGallery.category.social_organic",
  },
};

const ACCENT_DARK: Record<GalleryCategory, string> = {
  ads_ecommerce: "#be185d",
  ads_lead_generation: "#0e7490",
  video_talking_head: "#6d28d9",
  video_ugc_ecommerce: "#c0392b",
  video_ugc_software: "#1d4ed8",
  ads_graphic_ai: "#c2410c",
  social_graphic: "#9d174d",
  social_organic: "#166534",
};
function useCategoryItems(category: GalleryCategory, mediaType: GalleryMediaTypeFilter) {
  return useQuery<GalleryListResponse>({
    queryKey: ["viral-gallery", category, mediaType],
    queryFn: () => {
      const params = new URLSearchParams({ category, page: "1" });
      if (mediaType !== "all") params.set("mediaType", mediaType);
      return apiRequest("GET", `/api/viral-gallery?${params.toString()}`);
    },
    staleTime: 60_000,
  });
}

// ── thumbnail card ────────────────────────────────────────────────────
function Thumb({
  item,
  onClick,
  autoplay = false,
  contain = false,
}: {
  item: GalleryItem;
  onClick: (i: GalleryItem) => void;
  autoplay?: boolean;
  contain?: boolean;
}) {
  const isVideo = item.mediaType === "video";
  const fit = contain ? "object-contain" : "object-cover";
  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      className="group relative w-full h-full rounded-lg overflow-hidden cursor-pointer bg-black/30"
    >
      {item.signedUrl ? (
        isVideo ? (
          <video
            src={item.signedUrl}
            className={`w-full h-full ${fit} transition-transform duration-500 group-hover:scale-[1.04]`}
            muted
            loop
            playsInline
            autoPlay={autoplay}
            preload={autoplay ? "auto" : "metadata"}
            onMouseEnter={(e) => void (e.currentTarget as HTMLVideoElement).play().catch(() => {})}
            onMouseLeave={(e) => {
              if (!autoplay) (e.currentTarget as HTMLVideoElement).pause();
            }}
          />
        ) : (
          <img
            src={item.signedUrl}
            alt={item.filename}
            loading="lazy"
            className={`w-full h-full ${fit} transition-transform duration-500 group-hover:scale-[1.04]`}
          />
        )
      ) : (
        <div className="w-full h-full bg-white/[0.04]" />
      )}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/10 ring-1 ring-white/15 rounded-lg" />
    </button>
  );
}

// ── category section ──────────────────────────────────────────────────
function CategorySection({
  category,
  mediaType,
  onItemClick,
  onViewAll,
  isFiltered,
}: {
  category: GalleryCategory;
  mediaType: GalleryMediaTypeFilter;
  onItemClick: (item: GalleryItem) => void;
  onViewAll: (cat: GalleryCategory) => void;
  isFiltered: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const L = resolvedTheme === "light";
  const { t } = useTranslation();
  const { data, isLoading } = useCategoryItems(category, mediaType);
  const items = data?.items ?? [];
  const total = data?.total ?? items.length;
  const label = t(GALLERY_CATEGORY_LABEL_KEYS[category]);
  const style = CATEGORY_STYLE[category];
  const accentColor = L ? ACCENT_DARK[category] : style.accent;
  const isEmpty = !isLoading && items.length === 0;
  const bgColor = L ? style.bgLight : style.bg;
  const glowColor = L ? style.glowLight : style.glow;
  const fadeEndColor = L
    ? (style.bgLight.match(/#[0-9a-f]{6}/gi)?.[1] ?? "#fff")
    : (style.bg.match(/#[0-9a-f]{6}/gi)?.[1] ?? "#000");

  return (
    <div
      className="relative mb-3 mx-3 rounded-2xl overflow-hidden"
      style={{
        background: bgColor,
        boxShadow: L
          ? `inset 0 0 0 1px rgba(0,0,0,0.10), inset 0 0 0 1px ${accentColor}22`
          : undefined,
      }}
    >
      <div
        className="absolute bottom-0 left-0 w-[340px] h-[340px] rounded-full pointer-events-none"
        style={{ background: glowColor, filter: "blur(80px)", transform: "translate(-30%, 40%)" }}
      />

      {isFiltered ? (
        <div className="flex">
          <div className="w-64 shrink-0 flex flex-col justify-center px-8 py-10 relative z-10">
            <p
              className="text-[10px] uppercase tracking-[0.18em] font-bold mb-3"
              style={{ color: accentColor }}
            >
              {t("viralGallery.categoryLabel")}
            </p>
            <h2 className={`font-bold text-lg leading-snug ${L ? "text-gray-900" : "text-white"}`}>
              {label}
            </h2>
            <p className={`text-xs mt-2 leading-relaxed ${L ? "text-gray-500" : "text-white/40"}`}>
              {t(style.descKey)}
            </p>
            <p className={`text-xs mt-3 font-medium ${L ? "text-gray-600" : "text-white/40"}`}>
              {t("viralGallery.creations", { count: total })}
            </p>
          </div>
          <div
            className="flex-1 p-4 overflow-y-auto columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-3"
            style={{ maxHeight: "75vh" }}
          >
            {items.map((item) => (
              <div
                key={item.id}
                className="mb-3 break-inside-avoid rounded-xl overflow-hidden"
                style={{ aspectRatio: item.height > 0 ? `${item.width / item.height}` : "1" }}
              >
                <Thumb item={item} onClick={onItemClick} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex h-[320px]">
          <div className="w-72 shrink-0 flex flex-col justify-between px-8 py-7 relative z-10">
            <div>
              <h2
                className={`font-bold text-[22px] leading-tight ${L ? "text-gray-900" : "text-white"}`}
              >
                {label}
              </h2>
              <p
                className={`text-[12px] mt-3 leading-relaxed ${L ? "text-gray-500" : "text-white/45"}`}
              >
                {t(style.descKey)}
              </p>
            </div>

            <button
              onClick={() => onViewAll(category)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-[12px] w-fit transition-colors mt-4 ${L ? "bg-gray-900 hover:bg-gray-700 text-white" : "bg-white hover:bg-white/90 text-black"}`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              {t("viralGallery.browse")}
            </button>
          </div>

          <div className="flex-1 relative overflow-hidden p-2 pl-0">
            {isLoading ? (
              <div
                className={`flex items-center justify-center h-full ${L ? "text-gray-300" : "text-white/20"}`}
              >
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : isEmpty ? (
              <div className="flex items-center justify-center h-full">
                <p className={`text-sm font-medium ${L ? "text-gray-500" : "text-white/20"}`}>
                  {t("viralGallery.noItems")}
                </p>
              </div>
            ) : (
              <>
                <div className="absolute inset-2 flex gap-1.5 overflow-hidden">
                  {items.slice(0, 10).map((item) => {
                    const ratio = item.height > 0 ? item.width / item.height : 0.5625;
                    return (
                      <div
                        key={item.id}
                        className="shrink-0 h-full overflow-hidden rounded-lg"
                        style={{ width: `${Math.round(316 * ratio)}px` }}
                      >
                        <Thumb
                          item={item}
                          onClick={onItemClick}
                          autoplay={item.mediaType === "video"}
                        />
                      </div>
                    );
                  })}
                </div>

                <div
                  className="absolute right-0 top-0 bottom-0 w-24 pointer-events-none z-10"
                  style={{ background: `linear-gradient(to right, transparent, ${fadeEndColor})` }}
                />

                {total > 0 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
                    <button
                      onClick={() => onViewAll(category)}
                      className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#ef3e29] hover:bg-[#d9341f] text-white text-[11px] font-bold shadow-2xl transition-colors whitespace-nowrap"
                    >
                      {t("viralGallery.viewAll")} <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────
type ActiveCategory = "all" | GalleryCategory;

export default function ViralGalleryPage() {
  const { resolvedTheme } = useTheme();
  const L = resolvedTheme === "light";
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const urlCategory = new URLSearchParams(search).get("category") as ActiveCategory | null;
  const initialCategory: ActiveCategory =
    urlCategory &&
    (urlCategory === "all" || (GALLERY_CATEGORIES as readonly string[]).includes(urlCategory))
      ? urlCategory
      : "all";
  const urlMedia = new URLSearchParams(search).get("media") as GalleryMediaTypeFilter | null;
  const initialMedia: GalleryMediaTypeFilter =
    urlMedia && (GALLERY_MEDIA_TYPES as readonly string[]).includes(urlMedia) ? urlMedia : "all";
  const [active, setActive] = useState<ActiveCategory>(initialCategory);
  const [mediaFilter, setMediaFilter] = useState<GalleryMediaTypeFilter>(initialMedia);
  const [selected, setSelected] = useState<GalleryItem | null>(null);

  // Keep state in sync when sidebar (or back/forward) changes the URL
  useEffect(() => {
    const params = new URLSearchParams(search);
    const next = params.get("category") as ActiveCategory | null;
    const resolved: ActiveCategory =
      next && (next === "all" || (GALLERY_CATEGORIES as readonly string[]).includes(next))
        ? next
        : "all";
    setActive((prev) => (prev === resolved ? prev : resolved));
    const nextMedia = params.get("media") as GalleryMediaTypeFilter | null;
    const resolvedMedia: GalleryMediaTypeFilter =
      nextMedia && (GALLERY_MEDIA_TYPES as readonly string[]).includes(nextMedia)
        ? nextMedia
        : "all";
    setMediaFilter((prev) => (prev === resolvedMedia ? prev : resolvedMedia));
  }, [search]);

  const updateUrl = useCallback(
    (cat: ActiveCategory, media: GalleryMediaTypeFilter) => {
      const params = new URLSearchParams();
      if (cat !== "all") params.set("category", cat);
      if (media !== "all") params.set("media", media);
      const qs = params.toString();
      setLocation(qs ? `/viral?${qs}` : "/viral", { replace: true });
    },
    [setLocation]
  );

  const handleSelectCategory = useCallback(
    (cat: ActiveCategory) => {
      setActive(cat);
      updateUrl(cat, mediaFilter);
    },
    [mediaFilter, updateUrl]
  );

  const handleSelectMedia = useCallback(
    (media: GalleryMediaTypeFilter) => {
      setMediaFilter(media);
      updateUrl(active, media);
    },
    [active, updateUrl]
  );

  const { data: activeData } = useQuery<GalleryListResponse>({
    queryKey: ["viral-gallery", active, mediaFilter],
    queryFn: () => {
      const params = new URLSearchParams({ page: "1" });
      if (active !== "all") params.set("category", active);
      if (mediaFilter !== "all") params.set("mediaType", mediaFilter);
      return apiRequest("GET", `/api/viral-gallery?${params.toString()}`);
    },
  });
  const allItems = activeData?.items ?? [];
  const handleNavigate = useCallback((item: GalleryItem) => setSelected(item), []);

  // Pinned Stories sequences shown as a dedicated row at the top of the feed.
  const { data: sequencesData } = useQuery<{ sequences: SequenceListItem[] }>({
    queryKey: ["gallery-sequences", active],
    queryFn: () => {
      const params = new URLSearchParams();
      if (active !== "all") params.set("category", active);
      const qs = params.toString();
      return apiRequest(
        "GET",
        qs
          ? `/api/stories-creator/sequences/gallery?${qs}`
          : "/api/stories-creator/sequences/gallery"
      );
    },
  });
  const pinnedSequences = sequencesData?.sequences ?? [];
  const [openSeqId, setOpenSeqId] = useState<string | null>(null);

  return (
    <div className={`flex h-screen overflow-hidden ${L ? "bg-gray-50" : "bg-[#09090b]"}`}>
      <Sidebar />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Media-type filter */}
        <div className="px-6 pt-3 pb-1 flex items-center gap-2">
          <span
            className={`text-[11px] uppercase tracking-[0.18em] font-bold ${L ? "text-gray-500" : "text-white/45"}`}
          >
            {t("viralGallery.mediaFilter.label")}
          </span>
          <div className="flex gap-1.5">
            {GALLERY_MEDIA_TYPES.map((m) => {
              const isActive = mediaFilter === m;
              const label = t(`viralGallery.mediaFilter.${m}`);
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => handleSelectMedia(m)}
                  className={`text-[11px] px-3 py-1 rounded-full transition-colors ${
                    isActive
                      ? "bg-[#ef3e29] text-white"
                      : L
                        ? "viral-gallery-pill bg-white/60 text-gray-600 hover:bg-white"
                        : "bg-white/[0.06] text-white/60 hover:bg-white/[0.1] hover:text-white/90"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-3">
          {pinnedSequences.length > 0 && (
            <div className="px-6 mb-6">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-3 text-[#ef3e29]">
                Sekwencje Stories ({pinnedSequences.length})
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                {pinnedSequences.map((s) => (
                  <SequenceCard key={s.id} sequence={s} onOpen={setOpenSeqId} />
                ))}
              </div>
            </div>
          )}
          {active === "all" ? (
            GALLERY_CATEGORIES.map((cat) => (
              <CategorySection
                key={cat}
                category={cat}
                mediaType={mediaFilter}
                onItemClick={setSelected}
                onViewAll={(c) => handleSelectCategory(c)}
                isFiltered={false}
              />
            ))
          ) : (
            <CategorySection
              key={active}
              category={active as GalleryCategory}
              mediaType={mediaFilter}
              onItemClick={setSelected}
              onViewAll={() => {}}
              isFiltered={true}
            />
          )}
        </div>
      </main>

      {selected && (
        <AssetDetailModal
          item={selected}
          items={allItems}
          onClose={() => setSelected(null)}
          onNavigate={handleNavigate}
        />
      )}

      {openSeqId && (
        <SequenceDetailModal sequenceId={openSeqId} onClose={() => setOpenSeqId(null)} />
      )}
    </div>
  );
}
